import { Controller } from "@hotwired/stimulus";

// Modale « Devenir membre », saisie d'un code membre + POST AJAX vers
// /juniors/:id/membre_requests/secure_create. Le serveur répond JSON :
//   { success: true } ou { success: false, error: "..." }
export default class extends Controller {
  static targets = [
    "modal", "form", "input", "error", "success",
    "submitBtn", "submitLabel", "submitIcon", "actions",
  ];
  static values = { url: String };

  connect() {
    this._onKey = this._handleKey.bind(this);
    document.addEventListener("keydown", this._onKey);
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey);
  }

  open(event) {
    event?.preventDefault();
    if (!this.hasModalTarget) return;
    this._reset();
    this.modalTarget.hidden = false;
    requestAnimationFrame(() => {
      this.modalTarget.classList.add("bm-modal--visible");
      this.inputTarget?.focus();
    });
    document.body.classList.add("bm-no-scroll");
  }

  close(event) {
    event?.preventDefault();
    if (!this.hasModalTarget) return;
    this.modalTarget.classList.remove("bm-modal--visible");
    document.body.classList.remove("bm-no-scroll");
    setTimeout(() => {
      if (this.hasModalTarget) this.modalTarget.hidden = true;
    }, 200);
  }

  backdropClick(event) {
    if (event.target === this.modalTarget) this.close(event);
  }

  async submit(event) {
    event.preventDefault();
    const code = this.inputTarget.value.trim();
    if (!code) return;
    this._hideError();
    this._setSubmitting(true);

    try {
      const res = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({ code }),
      });

      let payload = null;
      try { payload = await res.json(); } catch (_) { /* non-json */ }

      if (res.ok && payload?.success) {
        this._showSuccess();
        return;
      }
      const msg = payload?.error || "La demande n'a pas pu être envoyée.";
      this._showError(msg);
    } catch (_) {
      this._showError("Connexion impossible. Vérifie ta connexion et réessaie.");
    } finally {
      this._setSubmitting(false);
    }
  }

  // ── Internals ────────────────────────────────────────────────────────

  _handleKey(event) {
    if (event.key === "Escape" && this.hasModalTarget && !this.modalTarget.hidden) {
      this.close();
    }
  }

  _setSubmitting(isSubmitting) {
    if (!this.hasSubmitBtnTarget) return;
    this.submitBtnTarget.disabled = isSubmitting;
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isSubmitting ? "Envoi…" : "Envoyer la demande";
    }
    if (this.hasSubmitIconTarget) {
      this.submitIconTarget.classList.toggle("fa-arrow-right", !isSubmitting);
      this.submitIconTarget.classList.toggle("fa-spinner", isSubmitting);
      this.submitIconTarget.classList.toggle("fa-spin", isSubmitting);
    }
  }

  _showError(msg) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
    this.inputTarget?.focus();
    this.inputTarget?.select();
  }

  _hideError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }

  _showSuccess() {
    if (this.hasSuccessTarget) this.successTarget.hidden = false;
    if (this.hasFormTarget) {
      // Hide the input + actions, leave only the success block + a Close button.
      this.inputTarget.hidden = true;
      const label = this.formTarget.querySelector('label[for="bm-code"]');
      if (label) label.hidden = true;
    }
    if (this.hasActionsTarget) {
      this.actionsTarget.innerHTML = "";
      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "ob-btn ob-btn--primary";
      closeBtn.textContent = "Super, merci";
      closeBtn.addEventListener("click", () => this.close());
      this.actionsTarget.appendChild(closeBtn);
    }
  }

  _reset() {
    this._hideError();
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
      this.inputTarget.hidden = false;
    }
    if (this.hasSuccessTarget) this.successTarget.hidden = true;
    const label = this.formTarget?.querySelector('label[for="bm-code"]');
    if (label) label.hidden = false;
  }

  _csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.getAttribute("content") : "";
  }
}
